import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { ChevronLeft, ChevronRight, Users } from "lucide-react";

function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function fmtDate(d) { return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }); }

const HOURS = Array.from({ length: 13 }, (_, i) => i + 8); // 8am–8pm

export default function TeamAvailability() {
  const { current } = useWorkspace() || {};
  const [members, setMembers]   = useState([]);
  const [events, setEvents]     = useState({});
  const [loading, setLoading]   = useState(true);
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    return d;
  });

  // Fetch all members: try org members first, fall back to workspace event creators
  useEffect(() => {
    if (!current?.workspace_id) return;
    const loadMembers = async () => {
      const allMembers = [];
      // 1. Try org members (business plan)
      try {
        const { data } = await api.get("/me/business/members");
        if (Array.isArray(data) && data.length) {
          allMembers.push(...data);
        }
      } catch {}
      // 2. Workspace event creators (always available)
      try {
        const { data } = await api.get(`/workspaces/${current.workspace_id}/members`);
        if (Array.isArray(data)) {
          data.forEach(m => {
            if (!allMembers.find(x => (x.user_id || x.id) === (m.user_id || m.id))) {
              allMembers.push(m);
            }
          });
        }
      } catch {}
      // 3. If still empty, show just the current user
      if (!allMembers.length) {
        try {
          const { data } = await api.get("/me/profile");
          if (data) allMembers.push(data);
        } catch {}
      }
      setMembers(allMembers);
    };
    loadMembers();
  }, [current?.workspace_id]);

  // Fetch busy/free slots for all known members
  useEffect(() => {
    if (!current?.workspace_id) { setLoading(false); return; }
    setLoading(true);
    const end = addDays(weekStart, 7);
    api.get(`/workspaces/${current.workspace_id}/events/availability`, {
      params: { start: weekStart.toISOString(), end: end.toISOString() }
    })
      .then(({ data }) => setEvents(data || {}))
      .catch(() => setEvents({}))
      .finally(() => setLoading(false));
  }, [current?.workspace_id, weekStart]);

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const isBusy = (memberId, day, hour) => {
    const evts = events[memberId] || [];
    const s = new Date(day); s.setHours(hour, 0, 0, 0);
    const e = new Date(day); e.setHours(hour + 1, 0, 0, 0);
    return evts.some(ev => new Date(ev.start) < e && new Date(ev.end) > s);
  };

  const memberId = (m) => m.user_id || m.id || m.member_id || m.email || m.invited_email;
  const memberName = (m) => m.name || m.display_name || m.invited_email || m.email || "Unknown";

  return (
    <div className="flex flex-col h-full bg-[#0A0A0A]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#2A2A2A] sticky top-0 bg-[#0A0A0A] z-10">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[#A1A1AA]" />
          <h2 className="text-[11px] font-semibold text-[#EDEDED] uppercase tracking-wider">Team Availability</h2>
          {members.length > 0 && (
            <span className="text-[10px] text-[#52525B]">({members.length} member{members.length !== 1 ? "s" : ""})</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setWeekStart(d => addDays(d, -7))}
            className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-[11px] text-[#A1A1AA]">
            {fmtDate(weekStart)} – {fmtDate(addDays(weekStart, 6))}
          </span>
          <button onClick={() => setWeekStart(d => addDays(d, 7))}
            className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full text-[#3F3F46] text-[11px]">Loading…</div>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-[#3F3F46]">
            <Users className="w-6 h-6 opacity-30" />
            <p className="text-[11px]">No team members found in this workspace</p>
          </div>
        ) : (
          <table className="w-full text-[10px] border-collapse" style={{ minWidth: 640 }}>
            <thead className="sticky top-0 bg-[#0A0A0A] z-10">
              <tr className="border-b border-[#2A2A2A]">
                <th className="w-28 px-3 py-2 text-left text-[#52525B] font-medium sticky left-0 bg-[#0A0A0A]">Member</th>
                {days.map((d, i) => (
                  <th key={i} colSpan={HOURS.length}
                    className={`px-1 py-2 text-center text-[10px] font-medium border-l border-[#1a1a1a] ${
                      d.toDateString() === new Date().toDateString()
                        ? "text-[#5A90F5]" : "text-[#52525B]"
                    }`}>
                    {d.toLocaleDateString("en-US", { weekday: "short", day: "numeric" })}
                  </th>
                ))}
              </tr>
              <tr className="border-b border-[#1a1a1a]">
                <th className="sticky left-0 bg-[#0A0A0A]" />
                {days.map((_, di) => HOURS.map(h => (
                  <th key={`${di}-${h}`} className="w-5 text-center text-[#2A2A2A] py-0.5 border-l border-[#1a1a1a] font-normal">
                    {h === 8 || h === 12 || h === 17 || h === 20 ? `${h}` : ""}
                  </th>
                )))}
              </tr>
            </thead>
            <tbody>
              {members.map(m => {
                const id = memberId(m);
                const name = memberName(m);
                const hasData = !!events[id];
                return (
                  <tr key={id} className="border-b border-[#111] hover:bg-[#0d0d0d] transition-colors">
                    <td className="px-3 py-1.5 text-[#A1A1AA] sticky left-0 bg-[#0A0A0A] truncate max-w-[112px] group-hover:bg-[#0d0d0d]">
                      <div className="flex items-center gap-1.5">
                        {m.picture ? (
                          <img src={m.picture} alt={name} className="w-5 h-5 rounded-full flex-shrink-0 object-cover border border-[#2A2A2A]" onError={e => { e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }} />
                        ) : null}
                        <span className="w-5 h-5 rounded-full bg-[#1a1a1a] border border-[#2A2A2A] flex items-center justify-center text-[8px] text-[#52525B] flex-shrink-0 uppercase"
                          style={{ display: m.picture ? "none" : "flex" }}>
                          {name.charAt(0)}
                        </span>
                        <span className="truncate">{name}</span>
                      </div>
                    </td>
                    {days.map((d, di) => HOURS.map(h => {
                      const busy = hasData && isBusy(id, d, h);
                      return (
                        <td key={`${di}-${h}`}
                          title={busy ? `${name} is busy at ${h}:00` : undefined}
                          className={`border-l border-[#111] transition-colors ${
                            busy ? "bg-[#FF3B30]/25" : hasData ? "bg-[#22C55E]/8" : "bg-[#1a1a1a]/20"
                          }`}
                        />
                      );
                    }))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Legend */}
      <div className="px-4 py-2 border-t border-[#1a1a1a] flex items-center gap-5 text-[10px] text-[#52525B]">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-[#FF3B30]/25 inline-block" /> Busy
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-[#22C55E]/8 border border-[#22C55E]/20 inline-block" /> Free
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-[#1a1a1a]/20 border border-[#1a1a1a] inline-block" /> No data
        </span>
      </div>
    </div>
  );
}
